import React, { useCallback, useEffect, useRef } from 'react';

import { Carousel } from 'react-responsive-carousel';

import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { makeStyles } from '@mui/styles';
import { cn } from '@repo/ui';
// VRG-777 — the same two predicates every other surface reading `products.images` uses.
// The extension is the only type signal the column carries; see `product-media.ts`.
import { getProductThumbnail, isVideoUrl } from '@repo/types';
import { border } from 'settings/tokens';

/**
 * VRG-777 — how long a pointer must stay DOWN AND STILL before it counts as a hold.
 *
 * 180ms is the gap between the two gestures this has to tell apart, and it is chosen
 * from how each one behaves rather than from taste. A swipe is already moving well
 * inside 180ms — that is what makes it a swipe — so it never reaches the timer. A
 * deliberate press to inspect a frame lasts far longer than 180ms, so it always does.
 * Shorter and a hesitant swipe would blink the video to a stop before moving; longer
 * and a press stops feeling like it did anything.
 */
const HOLD_MS = 180;

/**
 * How far a pointer may drift and still count as held.
 *
 * No finger is motionless, and a touch screen reports jitter of a few pixels on a
 * perfectly still thumb. Without a tolerance every hold would be read as a swipe the
 * moment the hardware noticed a tremor. 8px is below the distance at which the carousel
 * itself begins to treat a drag as a swipe, so the two never both claim the gesture.
 */
const HOLD_MOVE_TOLERANCE_PX = 8;

interface CarouselProps {
  images: string[];
  infiniteLoop?: boolean;
  showThumbs?: boolean;
  showArrows?: boolean;
  showStatus?: boolean;
  maxHeight?: number | string;
  fit?: 'cover' | 'contain';
  height?: number | string;
}

export const ImageCarousel = ({
  infiniteLoop = true,
  showThumbs = false,
  showArrows = false,
  showStatus = false,
  images,
  maxHeight = 320,
  fit = 'contain',
  height,
}: CarouselProps) => {
  const classes = useStyles();
  const hasImages = Boolean(images && images.length > 0);

  /**
   * Claim the hero transition name for exactly ONE image.
   *
   * This cannot be done inline on the slide with `index === 0`: with
   * `infiniteLoop` the carousel CLONES the first and last slides, so a name
   * rendered per-index lands on several DOM nodes at once. `view-transition-name`
   * must be unique document-wide, and a duplicate doesn't degrade — the browser
   * aborts the whole transition with "invalid state" and you get a hard cut with
   * no error surfaced to the user. Assigning imperatively after paint, and
   * clearing every other candidate, keeps the guarantee the API requires.
   */
  const rootRef = useRef<HTMLDivElement>(null);

  /*
   * VRG-777 — THE POSTER FRAME, and why every video gets the same one.
   *
   * `getProductThumbnail` returns the first non-video entry, which is the frame the
   * whole platform already shows for this product (its table cell, its picker row, its
   * outreach PNG). Using the same one here means the hero's standing-in frame is the
   * image the brand has already seen represent the product everywhere else, rather than
   * a second, differently-chosen still.
   *
   * It is not a nicety. `poster` is what the viewer sees when autoplay does not happen,
   * and the case that matters is LOW POWER MODE on iOS: it refuses autoplay outright,
   * whatever the attributes say, and with no poster the hero is a black rectangle where
   * the product should be. The product form refuses to save a video without a still
   * precisely so this is never empty (`ProductEditPage`); `undefined` here is only for
   * legacy rows that predate that rule.
   */
  const poster = getProductThumbnail(images) ?? undefined;
  const hasVideo = (images ?? []).some(isVideoUrl);

  /*
   * ── WHY PLAYBACK IS SYNCED FROM THE DOM RATHER THAN FROM AN INDEX ────────────────
   *
   * With `infiniteLoop` (the default) the carousel CLONES the first and last slides —
   * it clones the React elements handed to it, so a video in slot 0 exists TWICE in the
   * DOM, and both copies autoplay. Index-based control cannot fix that: the clone has
   * the same index as the original, so "play the video whose index is selected" plays
   * both. The only thing that distinguishes them is WHICH ONE the carousel has marked
   * `.slide.selected`, which is a DOM fact, so that is what this reads.
   *
   * It is also what makes every other case fall out for free: a video in a slot nobody
   * has swiped to is paused rather than decoding off-screen, and the snap-back from a
   * clone to the real slide needs no special handling because the class simply moves.
   *
   * ── WHY A MutationObserver AND NOT `onChange` + rAF ──────────────────────────────
   * The class is applied on the carousel's next render, so reacting to `onChange`
   * requires waiting a frame — and `requestAnimationFrame` does not fire at all in the
   * review environment this gets verified in, so the whole mechanism would look broken
   * exactly where it is looked at. A MutationObserver on the class attribute is a plain
   * DOM callback: it fires wherever the DOM mutates, needs no knowledge of the
   * carousel's internals, and cannot loop, because `play()` and `pause()` change no
   * classes.
   *
   * `autoPlay` is left ON the elements as well, deliberately. If this sync never runs,
   * every video plays — which still satisfies "a video in any slot plays" — and the
   * sync's only job is then to narrow that to the visible one. The degradation points
   * the right way: the failure of an optimisation, not of the feature.
   */
  const syncPlayback = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    root.querySelectorAll('video').forEach((video) => {
      const slide = video.closest('.slide');
      // No `.slide` ancestor means the carousel is not mounted the way we expect; leave
      // the element's own `autoPlay` to govern rather than pausing something we cannot
      // reason about.
      if (!slide) return;
      if (slide.classList.contains('selected')) {
        // A rejected `play()` is the normal, expected outcome under Low Power Mode and
        // under a browser that has decided this is not a permitted gesture. The poster
        // is already standing in, so there is nothing to recover and nothing to log.
        void video.play().catch(() => undefined);
      } else if (!video.paused) {
        video.pause();
        // Rewound so arriving at the slide starts the loop from its first frame rather
        // than from wherever it had reached while off-screen.
        video.currentTime = 0;
      }
    });
  }, []);

  /*
   * The press-and-hold gesture's state. A ref rather than state: nothing here renders,
   * and re-rendering a playing <video> mid-gesture would restart it.
   */
  const hold = useRef<{
    timer: ReturnType<typeof setTimeout> | null;
    x: number;
    y: number;
    paused: HTMLVideoElement | null;
  }>({ timer: null, x: 0, y: 0, paused: null });

  const clearHoldTimer = () => {
    if (hold.current.timer !== null) {
      clearTimeout(hold.current.timer);
      hold.current.timer = null;
    }
  };

  const beginHold = (event: React.PointerEvent<HTMLVideoElement>) => {
    const video = event.currentTarget;
    clearHoldTimer();
    hold.current.x = event.clientX;
    hold.current.y = event.clientY;
    hold.current.timer = setTimeout(() => {
      hold.current.timer = null;
      // Only pause something that is actually playing. Holding on a video that Low
      // Power Mode already refused to start must not record it as "paused by the
      // hold", or releasing would then PLAY it — a press would start a video the
      // device has declined to autoplay, which is the opposite of what a hold means.
      if (video.paused) return;
      video.pause();
      hold.current.paused = video;
    }, HOLD_MS);
  };

  const moveDuringHold = (event: React.PointerEvent<HTMLVideoElement>) => {
    const moved =
      Math.abs(event.clientX - hold.current.x) > HOLD_MOVE_TOLERANCE_PX ||
      Math.abs(event.clientY - hold.current.y) > HOLD_MOVE_TOLERANCE_PX;
    if (!moved) return;
    // This is a swipe, not a hold. Disarm, and undo the pause if the timer had already
    // fired — a slow swipe that dwells before moving must not leave the video stopped.
    endHold();
  };

  const endHold = () => {
    clearHoldTimer();
    const paused = hold.current.paused;
    hold.current.paused = null;
    if (!paused) return;
    // Resume only if this video is still in the selected slide. Releasing after a swipe
    // has carried us elsewhere must not start a video that is now off-screen — the
    // observer has already paused and rewound it, and playing it here would undo that.
    const slide = paused.closest('.slide');
    if (slide && !slide.classList.contains('selected')) return;
    void paused.play().catch(() => undefined);
  };

  // A hold armed when the page navigates away would fire into an unmounted element.
  useEffect(() => clearHoldTimer, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !hasVideo) return;

    syncPlayback();
    const observer = new MutationObserver(syncPlayback);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
    });
    return () => observer.disconnect();
  }, [hasVideo, syncPlayback, images]);

  return (
    <>
      <div
        ref={rootRef}
        style={{
          width: '100%',
          height,
          borderRadius: hasImages ? 0 : 12,
          overflow: 'hidden',
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: hasImages ? (undefined as any) : `1px solid ${border.subtle}`,
        }}
      >
        {hasImages ? (
          <Carousel
            showThumbs={showThumbs}
            infiniteLoop={infiniteLoop}
            showArrows={showArrows}
            showStatus={showStatus}
            /*
             * VRG-494 — NO INDICATOR FOR A SINGLE PHOTOGRAPH.
             *
             * A page control's entire content is "which of N, and how many N" — with
             * one image both halves are constant, so the row says nothing and is pure
             * ornament on the product shot. Most certificates carry exactly one photo,
             * so this is the COMMON case rather than an edge one, and the old styling
             * already recorded the symptom in its own comment: around a single dot the
             * indicator "collapses into what looks like a single dot wearing a white
             * ring — an unexplained ornament rather than a position indicator". The
             * treatment below fixes how the row looks; this fixes it being there at all.
             *
             * Suppressed at the CAROUSEL rather than with `display: none` in CSS so the
             * `<ul>` is never in the accessibility tree either — a one-item list
             * announced as page navigation is the same non-information, read aloud.
             */
            showIndicators={images.length > 1}
            /*
             * VRG-509 — MOUSE DRAG, so the gesture is exercisable where it is reviewed.
             *
             * `swipeable` already defaults to `true`, so TOUCH swipe has always worked on
             * a phone — verified against the installed react-responsive-carousel@3.2.23
             * (`defaultProps` carries `swipeable: true`) and by driving a real touch
             * gesture in the preview. Nothing here was broken.
             *
             * `emulateTouch` is the half that was missing. It is absent from that same
             * `defaultProps`, so it arrives `undefined`, and it is passed straight through
             * to react-easy-swipe as `allowMouseEvents` — whose `_onMouseDown` returns
             * immediately when it is false. So a mouse drag was a no-op, and the ONLY
             * place this carousel actually gets reviewed is a desktop preview pane driven
             * by a mouse. An interaction that cannot be exercised where it is reviewed is
             * one nobody notices breaking.
             *
             * Harmless on a touch device: `allowMouseEvents` only adds mousedown/mousemove
             * handlers alongside the touch ones, and a phone does not emit them.
             */
            emulateTouch
            className={cn(classes.carusel, 'h-[100vw]')}
          >
            {images.map((image, index) =>
              isVideoUrl(image) ? (
                /*
                 * VRG-777 — a video slide.
                 *
                 * ── THE SIZING IS ON THE WRAPPER, NOT THE VIDEO, AND THAT IS LOAD-BEARING ──
                 * `CertificatePage`'s desktop frame forces the media square with
                 * `'& .carousel img': { height: '100% !important' }` — a selector that names
                 * `img` and therefore does not reach a `<video>`. That page is not this
                 * component's to edit, so the video has to arrive correctly sized on its own.
                 *
                 * So the `h-[100vw]` that the `<img>` carries moves up one level, onto this
                 * wrapper — which IS the `.slide > div` the same stylesheet forces to
                 * `height: 100% !important` at md+. The result is that the wrapper obeys
                 * exactly the rules the image obeys, at both widths, with no new selector:
                 * 100vw square on a phone, the frame's height on desktop. The video then
                 * fills the wrapper absolutely, so it cannot introduce a height of its own.
                 */
                <div
                  key={index}
                  className='h-[100vw]'
                  style={{ width: '100%', position: 'relative' }}
                >
                  <video
                    src={image}
                    poster={poster}
                    /*
                     * `muted` is not a preference, it is the price of admission: iOS
                     * autoplays only a muted, inline video, so an unmuted one simply does
                     * not start. The product form's helper text says the audio track is
                     * ignored, so a brand does not author for sound and then wonder why
                     * the hero is silent.
                     *
                     * `playsInline` stops iOS Safari taking the video fullscreen the
                     * moment it plays, which would hijack the whole page from a carousel
                     * slide the user only swiped past.
                     *
                     * `autoPlay` stays on even though `syncPlayback` drives playback — see
                     * the note on that function for why the belt-and-braces points this way.
                     */
                    autoPlay
                    muted
                    loop
                    playsInline
                    /*
                     * The decode is deferred to the slide actually being selected
                     * (`syncPlayback` calls `play()`, which fetches what it needs), so a
                     * product with a clip in slot 3 does not spend a phone's bandwidth on
                     * it before anyone swipes there. `metadata` rather than `none` because
                     * the first frame has to be available the instant the slide arrives,
                     * and `auto` would defeat the point.
                     */
                    preload='metadata'
                    /*
                     * ── PRESS AND HOLD TO PAUSE, RELEASE TO RESUME ───────────────────────
                     *
                     * POINTER events, deliberately, and this is the whole trick for keeping
                     * the gesture out of the carousel's way. The carousel swipes via
                     * react-easy-swipe, which listens to touchstart/touchmove and (because
                     * `emulateTouch` is set) mousedown/mousemove. Pointer events are a
                     * separate event type that fires alongside those, so this handler never
                     * sees, consumes or cancels anything the carousel reads — no
                     * `preventDefault`, no `stopPropagation`, nothing to swallow a swipe.
                     *
                     * MOVEMENT IS WHAT DISCRIMINATES THE TWO. A hold is "pointer down and
                     * still": the pause is armed on a timer, and any movement past a few
                     * pixels disarms it. So a swipe that begins on the video never pauses —
                     * it has moved long before HOLD_MS — while a deliberate press on a
                     * still finger does. The threshold exists because no finger is
                     * genuinely motionless; without it every hold would be read as a swipe.
                     */
                    onPointerDown={(event) => beginHold(event)}
                    onPointerMove={(event) => moveDuringHold(event)}
                    onPointerUp={endHold}
                    onPointerCancel={endHold}
                    onPointerLeave={endHold}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: fit,
                      // The poster and the frames are drawn over whatever the root's
                      // white background is; a letterboxed portrait clip (`fit:
                      // 'contain'`) would otherwise show that white at the bars, which
                      // reads as a rendering fault rather than as letterboxing.
                      background: '#000',
                    }}
                  />
                </div>
              ) : (
                <div key={index} style={{ width: '100%' }}>
                  <img
                    className='h-[100vw] object-cover'
                    src={image}
                    alt={image}
                    style={{ objectFit: fit, width: '100%' }}
                  />
                </div>
              )
            )}
          </Carousel>
        ) : (
          <svg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 40 40' fill='none'>
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M33 10H7V30H33V10ZM8.01961 29V11H31.9804V29H8.01961ZM14.6471 16C14.6471 17.1046 13.7341 18 12.6078 18C11.4816 18 10.5686 17.1046 10.5686 16C10.5686 14.8954 11.4816 14 12.6078 14C13.7341 14 14.6471 14.8954 14.6471 16ZM10.0588 27V24.5L14.6471 20L16.6863 22L23.8235 15L29.9412 21V27H10.0588Z'
              fill='#8B8D8F'
              opacity={0.5}
            />
          </svg>
        )}
      </div>
    </>
  );
};

/*
 * VRG-509 — WHERE THE DOTS SIT, and why the number is derived rather than chosen.
 *
 * On the certificate hero the authenticity badge floats bottom-LEFT over the same
 * photograph and the dots sit bottom-CENTRE, so the two read as one band — and they
 * were on different baselines, the dots ~11px low, which read as unaligned rather
 * than as a deliberate offset.
 *
 * Both are anchored to the PHOTOGRAPH'S BOTTOM EDGE, which is what makes the two
 * comparable at all:
 *
 *   badge   `CertificatePage`'s chip wrapper is `bottom: 16` inside `mediaShotSx`,
 *           whose bottom IS the image's bottom edge — so the badge's own bottom edge
 *           lands 16px above the image. (It read `bottom: 32` against a `pb: '16px'`
 *           box until that padding was removed to sit the demo bar flush under the
 *           photograph; both spellings put the badge on the same pixel, which is why
 *           `BADGE_BOTTOM_INSET` did not change.)
 *   dots    `.control-dots` is `position: absolute` against `.carousel-slider`,
 *           whose bottom IS the image's bottom edge.
 *
 * So the badge's CENTRE is `BADGE_BOTTOM_INSET + BADGE_HEIGHT / 2` above the image,
 * and the dots row — `height: 16` with `align-items: center` — centres its dots on
 * `bottom + DOTS_ROW_HEIGHT / 2`. Setting those equal gives the constant below.
 *
 * ── WHY A CONSTANT AND NOT A MEASUREMENT OF THE BADGE ───────────────────────────
 * The dots must NOT be laid out relative to the badge, even though the number is
 * derived from it. The badge is not a fixed neighbour: it animates its width on
 * mount (circle -> pill -> label), and since VRG-494 it renders NOTHING at all for a
 * link-opened Connect certificate. Anything that measured it would move the dots as
 * it appeared, resized or vanished — a page control that shuffles while you read is
 * worse than one that is 11px off. A fixed value that happens to coincide with the
 * badge's centre gets the alignment without the coupling.
 *
 * It is also why the badge's height is written here as a number rather than imported:
 * an import would be a live dependency on a component this one must not depend on.
 * The value is MEASURED, not guessed — 29.91px in the browser at 375x812, which is
 * the sum of the pill's own parts (4px + 4px padding from `py: 0.5` on `apps/app`'s
 * 8px base, 1px + 1px border, and a 19.91px caption line box). It is the same in
 * every badge state: `verified`, `connected`, `unverified` and `pending` all put a
 * 16px icon slot beside that same caption, so the caption governs the height.
 *
 * The arithmetic lands on 22.955px and the rule below rounds to 23 — a 0.045px
 * residual, well under one device pixel at any DPR, and preferable to writing a
 * three-decimal figure that implies more precision than one measurement carries.
 */
const BADGE_BOTTOM_INSET = 16;
const BADGE_HEIGHT = 29.91;
const DOTS_ROW_HEIGHT = 16;
/** 16 + 29.91/2 - 16/2 = 22.955 -> 23. See the block above. */
const DOTS_BOTTOM = Math.round(
  BADGE_BOTTOM_INSET + BADGE_HEIGHT / 2 - DOTS_ROW_HEIGHT / 2
);

// No `theme` parameter any more: the dots are white and the scrim behind them is the
// only thing tuning their contrast, so nothing here reads the palette.
const useStyles = makeStyles(() => ({
  carusel: {
    '& .carousel-slider': {
      overflow: 'hidden',
    },
    /*
     * VRG-494 — the standard page control: a row of white dots, the current one an
     * elongated pill. User directive: "make them the standard like Apple pills
     * slideshow dots."
     *
     * ── THE CAPSULE IS GONE ─────────────────────────────────────────────────────
     * The dots used to sit in a translucent white capsule with a backdrop blur,
     * because a grey dot on a pale photograph disappears. That was the right fix for
     * a GREY dot; it is the wrong shape for this one. A capsule is a piece of UI
     * chrome laid over the product — it draws its own edge and reads as a control —
     * and no page control on a photograph, Apple's included, has one. With the dots
     * turned WHITE and lifted above the scrim (below) the capsule's job is done by
     * the scrim, which reads as photographic vignetting rather than as a component.
     *
     * ── LIFTED ABOVE THE SCRIM, and this is the load-bearing bit ────────────────
     * `mediaFrameSx`'s bottom scrim is an `::after` on the frame, so it PAINTS OVER
     * this row — the dots were being darkened by the same gradient that was supposed
     * to be helping them, which cancels out and leaves the contrast roughly where it
     * started. `zIndex: 2` puts the dots above it, so the gradient darkens only the
     * photograph behind them. That scrim's own docblock already claims it "picks up
     * the carousel dots"; this is what makes the claim true.
     *
     * ── WIDTH CARRIES THE CURRENT PAGE, and that reverses an earlier decision ───
     * The previous version deliberately used contrast alone, on the grounds that
     * "dots that grow and shrink make the row shuffle sideways as you swipe". The
     * observation is correct and the conclusion no longer follows: the row is
     * CENTRED, so a widening dot moves its neighbours by half the growth each,
     * symmetrically — 6px of travel split two ways, over 260ms, on a row that the eye
     * is not tracking because it is tracking the photograph. That is the motion every
     * iOS page control has, and it is what makes the position readable at a glance
     * rather than by comparing two greys.
     *
     * Contrast still moves with it (0.55 → 1.0). The pill is belt and braces, not a
     * replacement: width survives being viewed at arm's length, opacity survives
     * being viewed by someone who cannot see 6px of width difference.
     */
    '& .control-dots': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      // Read by `DOTS_BOTTOM`'s arithmetic — the row's height is half the reason the
      // dot lands where it does, so the two must not drift apart.
      height: DOTS_ROW_HEIGHT,
      margin: 0,
      // VRG-509 — was 12, which centred the dots 20px above the image's bottom edge
      // against the badge's 30.955px. See `DOTS_BOTTOM` above for the derivation.
      bottom: DOTS_BOTTOM,
      left: '50%',
      transform: 'translateX(-50%)',
      padding: 0,
      background: 'none',
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
      border: 'none',
      boxShadow: 'none',
      width: 'auto',
      // Above `mediaFrameSx`'s `::after` scrim — see the note above.
      zIndex: 2,
      '& .dot': {
        width: 6,
        height: 6,
        // NOT `50%`: the selected dot below is 18px wide and would render as an
        // ellipse. A radius larger than half the height is a capsule at any width, so
        // one value covers the circle and the pill, and the shape animates cleanly
        // between them instead of stepping.
        borderRadius: 999,
        background: '#fff',
        opacity: 0.55,
        boxShadow: 'none',
        margin: 0,
        transition:
          'width .26s cubic-bezier(0.32, 0.72, 0, 1), opacity .26s ease',
      },
      '& .selected': {
        width: 18,
        opacity: 1,
      },
      /*
       * The STATE still changes for someone who has asked the OS to stop moving
       * things — only the tween is dropped. Removing the width difference as well
       * would leave them a row of identical dots with no indication of position,
       * which is a worse outcome than the motion they opted out of.
       */
      '@media (prefers-reduced-motion: reduce)': {
        '& .dot': { transition: 'none' },
      },
    },
  },
}));
